/**
 * Wires `arrangements.model.ts` to its one read.
 *
 * Calls `fetchArrangements` and `useWorkThumbnails`, and nothing else: the
 * screen has no filters, no dialogs and no writes, so the only state it owns
 * beyond the model is the raw answer and the error. Every row links to the
 * work's record, where ending a mandate or recalling a consignment is done
 * with the terms in view.
 */

import { useEffect, useState } from "react";
import type { Arrangement } from "@ondo/shared";

import { fetchArrangements } from "../lib/api.ts";
import { useWorkThumbnails } from "../lib/useWorkThumbnails.ts";
import { describeArrangementsError, toArrangementsView } from "./arrangements.model.ts";
import type { ArrangementsView } from "./arrangements.model.ts";

export interface ArrangementsController {
  view: ArrangementsView;
}

/**
 * What is out, across every work the reader owns.
 *
 * ONE READ AND NO WRITES, and this time the sentence is the design rather than
 * a gap. Ending a mandate and recalling a consignment both name ONE work, and
 * both live on that work's record beside the terms they end; a control here
 * would be a second place to do the same thing, reached without the terms in
 * view. So every row links to the record and the screen changes nothing.
 *
 * @param workBase '/collector/me/work' — where a row links to.
 */
export function useArrangementsController(workBase: string): ArrangementsController {
  const [arrangements, setArrangements] = useState<Arrangement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const images = useWorkThumbnails(arrangements, (each) => ({
    id: each.workId,
    imagePath: each.imagePath,
  }));

  useEffect(() => {
    let active = true;

    fetchArrangements()
      .then((found) => {
        if (active) setArrangements(found);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeArrangementsError(cause));
      });

    return () => {
      active = false;
    };
  }, []);

  return {
    view: toArrangementsView({ arrangements, error, images, workBase }),
  };
}
